import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ExternalLink, Layers, Quote } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import PainPoints from "@/components/landing/PainPoints";
import FinalCTA from "@/components/landing/FinalCTA";
import CtaButton from "@/components/landing/CtaButton";
import TestimonialVideoPlayer from "@/components/landing/TestimonialVideoPlayer";
import { TESTIMONIALS } from "@/data/testimonials";
import {
  SIX_COMPONENTS,
  SIGNALS,
  DIFFERENTIALS,
  STEPS,
  buildFaqs,
  EOS_WORLDWIDE_URL,
  EOS_PROFILE_URL,
  CONTEXT_BULLETS,
} from "@/data/chileContent";

const inlineLink = "text-[#F26522] font-semibold underline decoration-[#F26522]/30 underline-offset-2 hover:decoration-[#F26522] transition-colors";
const arrowLink = "inline-flex items-center gap-1.5 text-[#F26522] font-semibold hover:underline";
const h2Class = "mt-3 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-navy-800 leading-tight";
const h2NoEyebrow = "font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-navy-800 leading-tight";
const bodyText = "text-slate-600 text-base leading-relaxed";
const strong = "font-semibold text-navy-800";

function buildSchema(c, faqs) {
  const url = `https://garcin.cl${c.path}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
          { "@type": "ListItem", position: 2, name: `EOS en ${c.name}`, item: url },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: c.seo.title,
        description: c.seo.description,
        inLanguage: c.locale.lang,
        isPartOf: { "@id": "https://garcin.cl/#website" },
        breadcrumb: { "@id": `${url}#breadcrumb` },
        about: { "@id": `${url}#service` },
        author: { "@id": "https://garcin.cl/#carlos-garcin" },
      },
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: `Implementación de EOS en ${c.name}`,
        serviceType: "Implementación de Entrepreneurial Operating System (EOS)",
        description: `Implementación de EOS® (Entrepreneurial Operating System®) con equipos de liderazgo de empresas emprendedoras en ${c.name}, de forma presencial o virtual.`,
        areaServed: { "@type": "Country", name: c.name },
        provider: { "@id": "https://garcin.cl/#carlos-garcin" },
        url,
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faqpage`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}

const BOLD_RE = /(\*\*[^*]+\*\*)/g;
function RichText({ text, boldClass }) {
  return text.split(BOLD_RE).map((p, i) =>
    p.startsWith("**") ? <strong key={i} className={boldClass}>{p.slice(2, -2)}</strong> : p
  );
}

function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

function Hero({ c }) {
  return (
    <section data-testid="country-hero" className="bg-[#0A1128] pt-28 pb-14">
      <div className="container-eos">
        <p className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
          EOS en {c.name} · Professional EOS Implementer®
        </p>
        <h1 className="mt-4 font-display font-extrabold text-white leading-[1.05] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
          EOS en {c.name}: una forma simple y probada de{" "}
          <span className="text-[#F26522]">dirigir mejor tu empresa.</span>
        </h1>
        <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
          {c.heroSubtitle}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Link to="/contacto" data-testid="country-hero-cta" className="btn-primary">
            Agendar una llamada de 15 minutos
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#que-es-eos-local"
            data-testid="country-hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white bg-white/5 font-semibold px-6 py-3 rounded-md transition-all duration-200 hover:bg-white/10 hover:border-white/60"
          >
            ¿Qué es EOS?
          </a>
        </div>
        <p className="mt-3 text-sm text-slate-400">Sin costo. Sin compromiso.</p>
      </div>
    </section>
  );
}

function DirectAnswer({ c }) {
  return (
    <section id="que-es-eos-local" data-testid="country-direct-answer" className="section-padding bg-white scroll-mt-20">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-start">
          <h2 className={`reveal lg:col-span-5 ${h2NoEyebrow}`}>¿Qué es EOS y quién lo implementa en {c.name}?</h2>
          <div className="reveal lg:col-span-7 space-y-4" style={{ transitionDelay: "100ms" }}>
            <p className="border-l-4 border-[#F26522] pl-5 font-display font-bold text-navy-800 text-lg sm:text-xl leading-snug">
              EOS — Entrepreneurial Operating System® — es un sistema de gestión para empresas emprendedoras que ayuda a sus equipos de liderazgo a lograr mayor claridad, disciplina y ejecución.
            </p>
            <p className={`pl-6 ${bodyText}`}>
              En {c.name}, trabajo directamente con empresarios y equipos de liderazgo como{" "}
              <Link to="/implementador-eos" className={inlineLink}>Professional EOS Implementer®</Link>
              {" "}certificado por EOS Worldwide, ayudándolos a implementar EOS de forma práctica en sus empresas.
            </p>
            <p className="pl-6">
              <Link to="/implementador-eos" data-testid="country-answer-implementador-link" className={arrowLink}>
                Conoce qué hace un Implementador EOS →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowEosHelps() {
  return (
    <section data-testid="country-six-components" className="section-padding bg-white">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <div className="reveal lg:col-span-5">
            <Eyebrow>El modelo EOS</Eyebrow>
            <h2 className={h2Class}>¿Cómo ayuda EOS a una empresa?</h2>
            <p className={`mt-4 ${bodyText}`}>
              EOS trabaja sobre{" "}
              <Link to="/el-modelo-eos" className={inlineLink}>los seis componentes de EOS</Link>
              . La idea es simple: cuando el equipo de liderazgo fortalece sistemáticamente estos seis componentes, la organización funciona con <strong className={strong}>mayor claridad, foco y disciplina</strong>.
            </p>
            <p className="mt-5">
              <Link to="/el-modelo-eos" data-testid="country-modelo-link" className={arrowLink}>
                Conocer el modelo EOS →
              </Link>
            </p>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-2 gap-x-5 gap-y-6 sm:gap-x-8">
            {SIX_COMPONENTS.map((c, i) => (
              <li
                key={c.name}
                data-testid={`country-component-${i + 1}`}
                className="reveal flex flex-col sm:flex-row items-start gap-2.5 sm:gap-3.5 border-t-2 border-slate-200 pt-4 hover:border-[#F26522] transition-colors"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-9 h-9 rounded-full bg-[#F26522]/10 flex items-center justify-center shrink-0">
                  <c.icon className="w-4 h-4 text-[#F26522]" strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-display font-bold text-navy-800">{c.name}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-snug">{c.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function LocalContext({ c }) {
  return (
    <section data-testid="country-context" className="section-padding bg-[#0A1128] text-white relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#F26522] opacity-10 blur-3xl pointer-events-none" />
      <div className="container-eos relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <div className="reveal lg:col-span-5">
            <Eyebrow>EOS en {c.name}</Eyebrow>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-[2.6rem] text-white leading-[1.1]">
              {c.context.titlePlain}{" "}
              <span className="text-[#F26522]">{c.context.titleOrange}</span>
            </h2>
          </div>
          <div className="reveal lg:col-span-7 space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed" style={{ transitionDelay: "100ms" }}>
            {c.context.paragraphs.map((p, i) => (
              <p key={i}><RichText text={p} boldClass="font-semibold text-white" /></p>
            ))}
          </div>
        </div>
        <ul data-testid="country-context-bullets" className="mt-8 lg:mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          {CONTEXT_BULLETS.map((b) => (
            <li key={b} className="reveal flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-white leading-snug">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-[#F26522] flex items-center justify-center shrink-0">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </span>
              {b}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FitSignals({ c }) {
  return (
    <section data-testid="country-fit" className="section-padding bg-white">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-14 items-start">
          <div className="reveal lg:col-span-5">
            <Eyebrow>Calce</Eyebrow>
            <h2 className={h2Class}>¿Para qué tipo de empresas en {c.name} puede hacer sentido EOS?</h2>
          </div>
          <p className={`reveal lg:col-span-7 lg:pt-8 ${bodyText}`}>
            EOS suele hacer más sentido en empresas que ya tienen un <strong className={strong}>equipo de liderazgo</strong>, están creciendo y sienten que la forma en que han operado hasta ahora comienza a quedar corta para la siguiente etapa.
          </p>
        </div>
        <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SIGNALS.map((s, i) => (
            <li key={i} className="reveal flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-slate-700 leading-snug" style={{ transitionDelay: `${(i % 3) * 60}ms` }}>
              <span className="mt-0.5 w-5 h-5 rounded-full bg-[#F26522]/10 flex items-center justify-center shrink-0">
                <Check className="w-3 h-3 text-[#F26522]" strokeWidth={3} />
              </span>
              {s}
            </li>
          ))}
        </ul>
        <div className="reveal mt-5 flex items-start gap-3 rounded-xl bg-[#FEF3EB] border-l-4 border-[#F26522] p-5">
          <Layers className="w-5 h-5 text-[#F26522] shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-slate-700 text-base leading-relaxed">
            {c.fitClosing}
          </p>
        </div>
      </div>
    </section>
  );
}

function AboutCarlos({ c }) {
  return (
    <section data-testid="country-about-carlos" className="section-padding bg-slate-50/60 border-y border-slate-100">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 lg:gap-12 items-start">
          <div className="lg:col-span-3 reveal">
            <img
              src="/implementador-eos/hero-real-simple-resultados.jpg"
              alt={`Carlos Garcín, Professional EOS Implementer en ${c.name}`}
              data-testid="country-carlos-image"
              loading="lazy"
              className="w-full max-w-[220px] lg:max-w-none rounded-2xl ring-1 ring-slate-200 shadow-[0_25px_60px_-20px_rgba(10,17,40,0.35)]"
            />
          </div>
          <div className="lg:col-span-9 reveal" style={{ transitionDelay: "100ms" }}>
            <Eyebrow>Quién implementa EOS en {c.name}</Eyebrow>
            <h2 className={h2Class}>¿Quién es Carlos Garcín?</h2>
            <p className={`mt-4 max-w-3xl ${bodyText}`}>
              Soy Professional EOS Implementer® certificado por{" "}
              <a href={EOS_WORLDWIDE_URL} target="_blank" rel="noopener noreferrer" className={inlineLink}>EOS Worldwide</a>
              {" "}y trabajo con empresarios y equipos de liderazgo en Chile y Latinoamérica. Antes de implementar EOS profesionalmente, viví el sistema desde dentro como ejecutivo, después de <strong className={strong}>más de 20 años</strong> trabajando en operaciones, tecnología, procesos y liderazgo de equipos.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-0 md:divide-x divide-slate-200 border-t border-slate-200 pt-5">
              {DIFFERENTIALS.map((d, i) => (
                <article key={d.title} data-testid={`country-differential-${i + 1}`} className="md:px-5 first:md:pl-0 last:md:pr-0">
                  <h3 className="font-display font-bold text-navy-800 leading-snug">{d.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d.text}</p>
                </article>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-8">
              <Link to="/implementador-eos" data-testid="country-historia-link" className={arrowLink}>
                Conocer la historia de Carlos →
              </Link>
              <a href={EOS_PROFILE_URL} target="_blank" rel="noopener noreferrer" data-testid="country-eos-profile-link" className={arrowLink}>
                Ver perfil oficial en EOS Worldwide
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonial() {
  const t = TESTIMONIALS[0];
  if (!t) return null;
  return (
    <section data-testid="country-testimonial" className="section-padding bg-[#FFF7F1]">
      <div className="container-eos">
        <div className="max-w-3xl reveal">
          <Eyebrow>Testimonio</Eyebrow>
          <h2 className={h2Class}>¿Cómo se vive una implementación de EOS desde dentro?</h2>
        </div>
        <div className="reveal mt-7 rounded-3xl bg-white ring-1 ring-[#F26522]/15 shadow-[0_30px_70px_-30px_rgba(242,101,34,0.35)] p-6 sm:p-8 lg:p-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-3">
            <TestimonialVideoPlayer videoId={t.videoId} posterImage={t.poster} name={t.name} />
          </div>
          <div className="lg:col-span-9 relative">
            <Quote aria-hidden="true" className="w-12 h-12 sm:w-16 sm:h-16 text-[#F26522]/20 -mb-3" fill="currentColor" strokeWidth={0} />
            <blockquote>
              <p data-testid="country-testimonial-quote" className="font-display font-extrabold text-navy-800 leading-[1.1] text-2xl sm:text-4xl lg:text-5xl">
                “{t.quotes[0]}”
              </p>
            </blockquote>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <p data-testid="country-testimonial-name" className="font-display font-bold text-lg text-navy-800">{t.name}</p>
                <p className="text-sm sm:text-base text-slate-500">
                  {t.role} · <span className="text-[#F26522] font-semibold">Soho</span>
                </p>
              </div>
              <CtaButton testId="country-testimonial-cta" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowToStart() {
  return (
    <section data-testid="country-how-to-start" className="section-padding bg-slate-50/60 border-y border-slate-100">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-end">
          <div className="reveal lg:col-span-8">
            <Eyebrow>Cómo comenzar</Eyebrow>
            <h2 className={h2Class}>¿Cómo saber si EOS puede funcionar en tu empresa?</h2>
            <p className={`mt-4 ${bodyText}`}>
              El primer paso es simple: <strong className={strong}>una conversación de 15 minutos</strong>. Me cuentas brevemente sobre tu empresa, tu equipo y los principales desafíos que están enfrentando, y vemos si tiene sentido seguir conversando sobre EOS.
            </p>
          </div>
          <div className="reveal lg:col-span-4 lg:justify-self-end">
            <CtaButton testId="country-steps-cta" />
          </div>
        </div>
        <ol className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8">
          {STEPS.map((s, i) => (
            <li key={s.title} data-testid={`country-step-${i + 1}`} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#F26522] text-white font-display font-bold flex items-center justify-center shrink-0 shadow-md shadow-[#F26522]/25">
                  {i + 1}
                </span>
                {i < STEPS.length - 1 && <span aria-hidden="true" className="hidden md:block h-px flex-1 bg-slate-300" />}
              </div>
              <h3 className="mt-4 font-display font-bold text-navy-800 text-lg leading-snug">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CountryFaq({ c, faqs }) {
  return (
    <section data-testid="country-faq" className="section-padding bg-white">
      <div className="container-eos">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-start">
          <div className="reveal lg:col-span-4 lg:sticky lg:top-28">
            <Eyebrow>Preguntas frecuentes</Eyebrow>
            <h2 className={h2Class}>Preguntas frecuentes sobre EOS en {c.name}</h2>
            <p className="mt-3 text-slate-600">
              Si buscas más detalle, revisa las{" "}
              <Link to="/faq" data-testid="country-faq-link" className={inlineLink}>preguntas frecuentes sobre EOS</Link>.
            </p>
            <div className="mt-6 hidden lg:block">
              <CtaButton testId="country-faq-cta" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="border-t border-slate-200">
              {faqs.map((f) => (
                <details key={f.id} data-testid={`country-faq-item-${f.id}`} className="group border-b border-slate-200">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 py-4 [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F26522] rounded-sm">
                    <h3 className="font-display font-bold text-navy-800 text-base sm:text-lg leading-snug group-hover:text-[#F26522] group-open:text-[#F26522] transition-colors">
                      {f.q}
                    </h3>
                    <ChevronDown aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 group-open:rotate-180 group-open:text-[#F26522]" />
                  </summary>
                  <p className="pb-5 pr-2 sm:pr-10 text-slate-600 text-base leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-6 lg:hidden">
              <CtaButton testId="country-faq-cta-mobile" />
            </div>
          </div>
        </div>
        <p data-testid="country-authorship" className="mt-10 text-sm text-slate-500">
          Contenido revisado por{" "}
          <Link to="/implementador-eos" className="font-semibold text-navy-800 hover:text-[#F26522] hover:underline">Carlos Garcín</Link>
          {" "}· Professional EOS Implementer®
        </p>
      </div>
    </section>
  );
}

export default function CountryLanding({ country: c }) {
  useReveal();
  const faqs = buildFaqs(c);

  useEffect(() => {
    setPageSEO({
      title: c.seo.title,
      description: c.seo.description,
      path: c.path,
      keywords: c.seo.keywords,
      locale: c.locale,
      imageAlt: `Carlos Garcín — Implementador EOS ${c.name}`,
    });
    window.scrollTo(0, 0);
  }, [c]);

  return (
    <>
      <Header />
      <main className="implementador-main country-main">
        <Hero c={c} />
        <DirectAnswer c={c} />
        <PainPoints />
        <HowEosHelps />
        <LocalContext c={c} />
        <FitSignals c={c} />
        <AboutCarlos c={c} />
        <Testimonial />
        <HowToStart />
        <CountryFaq c={c} faqs={faqs} />
        <FinalCTA
          eyebrow={`EOS en ${c.name}`}
          title={<>¿Quieres saber si EOS puede <span className="text-[#F26522]">ayudar a tu empresa{c.finalCtaSuffix}?</span></>}
          text="Conversemos 15 minutos sobre tu empresa, lo que están tratando de lograr y los desafíos que están enfrentando. Si EOS puede hacer sentido, te explicaré cuál sería el siguiente paso."
          microcopy="Sin costo. Sin compromiso."
        />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildSchema(c, faqs)) }} />
    </>
  );
}
